'use client';

import type { ScheduledReportRule } from '@terra/shared-contracts-applications';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Tick01Icon, ArrowDown01Icon, Delete01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { automationId } from '@terra/shared-utils';
import { IconButton } from '../../../components/icon-button';
import { Select, createSelectListCollection } from '../../../components/select';
import { MetaDefault } from '../../../components/typography';
import {
  buildTimezoneSelectItems,
  formatHourLabel,
  getBrowserTimezone,
  resolveScheduleTimezoneSelection,
} from '../../automation/automationScheduleShared';
import { SettingsEditDialog } from '../SettingsEditDialog';
import { SettingsRow } from '../SettingsRow';
import { ScheduledReportFrequencyFields } from './ScheduledReportFrequencyFields';
import {
  SCHEDULED_REPORT_HOUR_OPTIONS,
  getScheduledReportRuleFieldErrors,
} from './scheduled-report-rule.utils';

export type ScheduledReportRuleBlockProps = {
  rule: ScheduledReportRule;
  disabled?: boolean;
  isUpdating?: boolean;
  canRemove?: boolean;
  onChange: (rule: ScheduledReportRule) => void;
  onRemove?: () => void;
  e2eAutomationIds?: {
    monthlyDaySelect?: string;
    hourEdit?: string;
    hourDialog?: string;
    hourSelect?: string;
    hourSave?: string;
    timezoneEdit?: string;
    timezoneDialog?: string;
    timezoneSelect?: string;
    timezoneSave?: string;
  };
};

export const ScheduledReportRuleBlock = ({
  rule,
  disabled,
  isUpdating,
  canRemove,
  onChange,
  onRemove,
  e2eAutomationIds,
}: ScheduledReportRuleBlockProps) => {
  const t = useTranslations('designSystem.automation.scheduledReports');
  const tSchedule = useTranslations('designSystem.automation.schedule');
  const tCommon = useTranslations('designSystem.common');
  const [isHourDialogOpen, setIsHourDialogOpen] = useState(false);
  const [isTimezoneDialogOpen, setIsTimezoneDialogOpen] = useState(false);
  const [hourDraft, setHourDraft] = useState(rule.hour);
  const [timezoneDraft, setTimezoneDraft] = useState(rule.timezone);
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const selectedTimezone = resolveScheduleTimezoneSelection({
    scheduleTimezone: rule.timezone,
    browserTimezone,
  });
  const timezoneItems = buildTimezoneSelectItems({
    browserTimezone,
    selectedTimezone,
  });
  const timezoneCollection = createSelectListCollection({
    items: timezoneItems,
  });
  const selectedTimezoneLabel =
    timezoneItems.find((item) => item.value === selectedTimezone)?.label ??
    selectedTimezone;
  const fieldErrors = getScheduledReportRuleFieldErrors(rule);
  const hourItems = useMemo(
    () =>
      SCHEDULED_REPORT_HOUR_OPTIONS.map((hour) => ({
        value: String(hour),
        label: formatHourLabel(hour),
      })),
    [],
  );
  const hourCollection = useMemo(
    () => createSelectListCollection({ items: hourItems }),
    [hourItems],
  );
  const isControlDisabled = Boolean(disabled || isUpdating);

  return (
    <VStack alignItems="stretch" gap={3} w="full">
      <HStack
        justifyContent="space-between"
        alignItems="center"
        w="full"
        gap={3}
      >
        <MetaDefault color="fg.neutral.muted">
          {t(`frequencies.${rule.frequency}`)}
        </MetaDefault>
        {canRemove ? (
          <IconButton
            aria-label={t('removeSchedule')}
            variant="ghost"
            size="sm"
            disabled={isControlDisabled}
            onClick={onRemove}
          >
            <Delete01Icon size={16} />
          </IconButton>
        ) : null}
      </HStack>

      <ScheduledReportFrequencyFields
        rule={rule}
        disabled={isControlDisabled}
        fieldErrors={fieldErrors}
        onChange={onChange}
        monthlyDaySelectAutomationId={e2eAutomationIds?.monthlyDaySelect}
      />

      <SettingsRow
        label={t('hour')}
        value={formatHourLabel(rule.hour)}
        action={{
          label: tCommon('edit'),
          onClick: () => {
            setHourDraft(rule.hour);
            setIsHourDialogOpen(true);
          },
          disabled: isControlDisabled,
          testId: e2eAutomationIds?.hourEdit,
        }}
        isReadOnly={disabled}
      />

      <SettingsRow
        label={tSchedule('timezone')}
        value={selectedTimezoneLabel}
        action={{
          label: tCommon('edit'),
          onClick: () => {
            setTimezoneDraft(selectedTimezone);
            setIsTimezoneDialogOpen(true);
          },
          disabled: isControlDisabled,
          testId: e2eAutomationIds?.timezoneEdit,
        }}
        isReadOnly={disabled}
      />

      <SettingsEditDialog
        open={isHourDialogOpen}
        title={t('hour')}
        onOpenChange={setIsHourDialogOpen}
        onSubmit={() => {
          onChange({ ...rule, hour: hourDraft });
          setIsHourDialogOpen(false);
        }}
        isSubmitDisabled={hourDraft === rule.hour}
        submitText={tCommon('saveChanges')}
        contentAutomationId={e2eAutomationIds?.hourDialog}
        submitAutomationId={e2eAutomationIds?.hourSave}
      >
        <Box
          w="full"
          {...(e2eAutomationIds?.hourSelect
            ? automationId(e2eAutomationIds.hourSelect)
            : {})}
        >
          <Select.Root
            collection={hourCollection}
            positioning={{ sameWidth: true }}
            value={[String(hourDraft)]}
            onValueChange={(details) => {
              const value = details.value[0];

              if (!value) {
                return;
              }

              setHourDraft(Number.parseInt(value, 10));
            }}
            size="sm"
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText />
                <ArrowDown01Icon size={14} />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                <Select.ItemGroup maxH="240px" overflowY="auto">
                  {hourItems.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      <Select.ItemText>{item.label}</Select.ItemText>
                      <Select.ItemIndicator>
                        <Tick01Icon size={16} />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.ItemGroup>
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Box>
      </SettingsEditDialog>

      <SettingsEditDialog
        open={isTimezoneDialogOpen}
        title={tSchedule('timezone')}
        onOpenChange={setIsTimezoneDialogOpen}
        onSubmit={() => {
          onChange({ ...rule, timezone: timezoneDraft });
          setIsTimezoneDialogOpen(false);
        }}
        isSubmitDisabled={timezoneDraft === selectedTimezone}
        submitText={tCommon('saveChanges')}
        contentAutomationId={e2eAutomationIds?.timezoneDialog}
        submitAutomationId={e2eAutomationIds?.timezoneSave}
      >
        <Box
          w="full"
          {...(e2eAutomationIds?.timezoneSelect
            ? automationId(e2eAutomationIds.timezoneSelect)
            : {})}
        >
          <Select.Root
            collection={timezoneCollection}
            positioning={{ sameWidth: true }}
            value={[timezoneDraft]}
            onValueChange={(details) => {
              const value = details.value[0];

              if (value) {
                setTimezoneDraft(value);
              }
            }}
            size="sm"
          >
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText placeholder={tSchedule('selectTimezone')} />
                <ArrowDown01Icon size={14} />
              </Select.Trigger>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                <Select.ItemGroup maxH="240px" overflowY="auto">
                  {timezoneItems.map((item) => (
                    <Select.Item key={item.value} item={item}>
                      <Select.ItemText>{item.label}</Select.ItemText>
                      <Select.ItemIndicator>
                        <Tick01Icon size={16} />
                      </Select.ItemIndicator>
                    </Select.Item>
                  ))}
                </Select.ItemGroup>
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </Box>
      </SettingsEditDialog>
    </VStack>
  );
};
